import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Autocomplete,
} from "@mui/material";

import { Group } from "types";
import CustomNumberInput from "components/CustomNumberInput";
import { useSession } from "providers/SessionProvider";
import { getUserFullName } from "helpers";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: Group;
  onEdit: (group: Group) => void;
};

export default function EditGroupModal({
  open = true,
  handleOpen,
  value,
  onEdit,
}: Props) {
  const { clients, users } = useSession();
  const [data, setData] = useState<Group>(value);

  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setData(value);
      const client = clients.find((c) => c.id === value.client_id);
      setSelectedClient({
        id: client?.id || 0,
        label: `${client?.code} - ${client?.razon_social}`,
      });
    }
  }, [open, value]);

  const options = useMemo(() => {
    const clientList = clients.map((client) => ({
      id: client.id,
      label: `${client.code} - ${client.razon_social}`,
    }));
    return clientList;
  }, [clients]);

  const handleChange = <K extends keyof Omit<Group, "id">>(
    name: K,
    value: Omit<Group, "id">[K],
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const onSubmit = (updatedGroup: Group) => {
    onEdit({
      ...updatedGroup,
      client: clients.find((c) => c.id === updatedGroup.client_id) || null,
    });
    handleOpen();
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "30rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h5" gutterBottom>
            Editar grupo de artículos
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Código"
              type="text"
              fullWidth
              onChange={(e) => handleChange("code", e.target.value)}
              value={data.code}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="outlined-required"
              label="Descripción"
              fullWidth
              value={data.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                id="client"
                autoComplete
                value={selectedClient}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={options}
                disabled
                onChange={(
                  e,
                  value: {
                    id: number;
                    label: string;
                  } | null,
                ) => {
                  handleChange("client_id", value?.id || null);
                  setSelectedClient(value || null);
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} required label="Cliente" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Creado por"
              value={
                data.created_by
                  ? getUserFullName(data.created_by, users)
                  : "SISTEMA"
              }
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!data.code || !data.description || !data.client_id}
              onClick={() => {
                onSubmit(data);
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
